import { Box, Flex, Group, Paper, Skeleton, Stack } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { PromoStripSkeleton } from "./PromotionBanner"
import catalog from "./catalog.module.css"
import classes from "./ProductListSkeleton.module.css"
import { readStoredViewMode } from "./utils"

// How much placeholder to lay down: enough to fill the fold at 1440 (three
// rows of four cards), not a full page. The frames reuse the real catalogue
// classes (catalog.module.css) so the swap to data lands on the same geometry.
const GRID_CARDS = 12
const LIST_ROWS = 8
const CATEGORY_ROWS = 8
const STOCK_ROWS = 3

/** Mirrors ProductCard: image, brand line, 2-line name, price, source, stock row, action. */
export function ProductCardSkeleton() {
  return (
    <Box className={`${catalog.productCard} ${classes.static}`}>
      <Skeleton className={catalog.imageBox} radius="md" />
      <Skeleton h={14} w="40%" radius="sm" mt={4} />
      <Skeleton h={16} radius="sm" />
      <Skeleton h={16} w="70%" radius="sm" />
      <Skeleton h={22} w="50%" radius="sm" mt={4} />
      <Skeleton h={14} w="60%" radius="sm" />
      <Flex align="center" gap="sm" mt={4}>
        <Skeleton h={14} w={56} radius="sm" />
        <Box className={classes.spacer} />
        <Skeleton h={14} w={96} radius="sm" />
      </Flex>
      <Skeleton h={36} radius="md" mt={4} />
    </Box>
  )
}

/** Mirrors a catalogue table row (canvas OrdB_CatalogTable). */
export function ProductListItemSkeleton() {
  return (
    <Flex align="center" gap="md" className={classes.tableRow}>
      <Skeleton h={48} w={48} radius="md" />
      <Box className={classes.spacer}>
        <Skeleton h={16} w="55%" radius="sm" />
        <Skeleton h={14} w="30%" radius="sm" mt={6} />
      </Box>
      <Skeleton h={16} w={96} radius="sm" />
      <Skeleton h={16} w={72} radius="sm" />
      <Skeleton h={36} w={132} radius="md" />
    </Flex>
  )
}

/** Mirrors SearchBar: search field, filter buttons, sort select. */
export function SearchBarSkeleton() {
  return (
    <Box className={catalog.toolbar}>
      <Skeleton h={42} radius="md" className={classes.searchInput} />
      <Skeleton h={42} w={124} radius="md" />
      <Skeleton h={42} w={96} radius="md" />
      <Skeleton h={42} w={96} radius="md" />
      <Skeleton h={42} w={176} radius="md" />
    </Box>
  )
}

/** Mirrors FilterSidebar: category list, price range, stock legend. */
export function FilterSidebarSkeleton() {
  return (
    <Box className={`${catalog.sidebar} ${classes.static}`}>
      <Stack gap="xs">
        <Skeleton h={18} w={80} radius="sm" />
        {Array.from({ length: CATEGORY_ROWS }).map((_, i) => (
          <Flex key={i} align="center" justify="space-between" gap="sm" className={classes.navRow}>
            <Skeleton h={14} w={`${78 - (i % 3) * 14}%`} radius="sm" />
            <Skeleton h={14} w={24} radius="sm" />
          </Flex>
        ))}
      </Stack>

      <Stack gap="xs">
        <Skeleton h={18} w={64} radius="sm" />
        <Group gap="sm" grow>
          <Skeleton h={36} radius="md" />
          <Skeleton h={36} radius="md" />
        </Group>
      </Stack>

      <Stack gap="xs">
        <Skeleton h={18} w={56} radius="sm" />
        {Array.from({ length: STOCK_ROWS }).map((_, i) => (
          <Flex key={i} align="center" justify="space-between" gap="sm" className={classes.navRow}>
            <Skeleton h={14} w={`${60 - i * 8}%`} radius="sm" />
            <Skeleton h={14} w={28} radius="sm" />
          </Flex>
        ))}
      </Stack>
    </Box>
  )
}

export function ProductGridSkeleton({ viewMode = "grid" }: { viewMode?: "grid" | "list" }) {
  if (viewMode === "list") {
    return (
      <Paper withBorder radius="lg" className={classes.static}>
        {Array.from({ length: LIST_ROWS }).map((_, i) => (
          <ProductListItemSkeleton key={i} />
        ))}
      </Paper>
    )
  }

  return (
    <Box className={catalog.productGrid}>
      {Array.from({ length: GRID_CARDS }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </Box>
  )
}

type Props = {
  /** Mirrors the real list's view toggle so the two states line up. Defaults to
   *  the mode persisted for this list, which is what the real page will pick. */
  viewMode?: "grid" | "list"
  /** Promotions known to reach this buyer: draw their strip so nothing moves when it lands. */
  promoCount?: number
}

/** The brand logo row above the grid. */
function BrandStripSkeleton() {
  return (
    <Group gap={12} wrap="nowrap" className={`${classes.static} ${classes.brandRow}`}>
      {Array.from({ length: 12 }).map((_, i) => (
        <Stack key={i} gap={4} align="center" w={92} className={classes.brandTile}>
          <Skeleton h={52} w={72} radius="md" />
          <Skeleton h={14} w={56} radius="sm" />
          <Skeleton h={14} w={20} radius="sm" />
        </Stack>
      ))}
    </Group>
  )
}

/**
 * Full-body placeholder for the buyer catalogue (canvas OrdB_Catalog): the
 * filter rail beside the toolbar, result count and product grid. Table view is
 * full width with its filters in a drawer, as on the real page.
 */
export function ProductListSkeleton({ viewMode, promoCount = 0 }: Props) {
  const { t } = useTranslation("common")
  const isMobile = useMediaQuery("(max-width: 62em)")
  const mode = viewMode ?? readStoredViewMode()
  const full = mode === "list" || isMobile

  // The placeholders carry no information; a screen reader hears "loading" once.
  return (
    <Box className={classes.root} role="status" aria-busy="true" aria-live="polite" aria-label={t("loading")}>
      {mode === "grid" && promoCount > 0 && (
        <Box mb="md">
          <PromoStripSkeleton count={promoCount} />
        </Box>
      )}
      <Box className={catalog.layout} data-full={full || undefined} aria-hidden="true">
        {!full && <FilterSidebarSkeleton />}

        <Stack gap="md" miw={0}>
          <SearchBarSkeleton />
          {mode === "grid" && <BrandStripSkeleton />}
          <Group justify="space-between" gap="sm">
            <Skeleton h={16} w={140} radius="sm" />
            <Skeleton h={16} w={96} radius="sm" />
          </Group>
          <ProductGridSkeleton viewMode={mode} />
        </Stack>
      </Box>
    </Box>
  )
}
